import { useEffect, useRef } from "react";
import { P, FF } from "../styles/tokens";
import { MESES } from "../constants/months";
import { ArboLogo } from "../components/ui";
import SecTabs from "../components/navigation/SecTabs";
import MobileHero from "../components/navigation/MobileHero";
import MobileBottomNav from "../components/navigation/MobileBottomNav";
import { useNavigation } from "../contexts/NavigationContext";
import { useFinancial } from "../contexts/FinancialContext";
import { fmt } from "../utils/format";
import ContentRouter from "./ContentRouter";
import ErrorBoundary from "../components/ui/ErrorBoundary";
import DeletionBanner from "../components/views/DeletionBanner";

export default function MobileLayout() {
  const { view, activeMonth, section, setShowMonthPicker } = useNavigation();
  const { year, capitalReal, hasCuentas, flujoDisponible, comprometido } = useFinancial();

  // Scroll tracking — useRef + DOM directo para evitar re-render por frame
  const rafRef = useRef(null);
  const heroRef = useRef(null);
  const compactRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const p = Math.min(Math.max(window.scrollY / 150, 0), 1);
        // Hero: fade out + slide up — nunca display:none
        if (heroRef.current) {
          heroRef.current.style.opacity = 1 - p;
          heroRef.current.style.transform = `translateY(${-p * 12}px)`;
          heroRef.current.style.pointerEvents = p > 0.95 ? "none" : "";
        }
        // Compact bar: fade in + slide down — siempre en DOM, solo opacity/transform
        if (compactRef.current) {
          const compactP = Math.min(Math.max((p - 0.6) / 0.4, 0), 1);
          compactRef.current.style.opacity = compactP;
          compactRef.current.style.transform = `translateY(${(1 - compactP) * -8}px)`;
          compactRef.current.style.pointerEvents = compactP < 0.1 ? "none" : "";
        }
      });
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const showHero = view === "mes" && section !== "editar";

  return (
    <div style={{ paddingBottom:100 }}>
      <DeletionBanner />
      {/* Sticky header: top bar + barra compacta */}
      <div style={{ background:P.surface, position:"sticky", top:0, zIndex:10, paddingTop:"env(safe-area-inset-top)" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 16px 0" }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <ArboLogo size={28} showText={false} />
            <span style={{ fontSize:20, fontWeight:900, color:P.text, fontFamily:FF, letterSpacing:"-0.5px" }}>arbo</span>
          </div>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            {view==="mes" && (
              <button onClick={()=>setShowMonthPicker(true)}
                style={{ padding:"6px 12px", borderRadius:99, border:`1.5px solid ${P.border}`, background:P.bg, color:P.text, cursor:"pointer", fontFamily:FF, fontSize:12, fontWeight:700, display:"flex", alignItems:"center", gap:4 }}>
                {MESES[activeMonth]} ▾
              </button>
            )}
            {view!=="mes" && (
              <span style={{ fontSize:16, fontWeight:900, color:P.text, fontFamily:FF }}>
                {view==="anual"?`${year}`:view==="config"?"Config":""}
              </span>
            )}
            <button aria-label="Notificaciones" style={{ width:44, height:44, borderRadius:99, background:P.bg, border:`1.5px solid ${P.border}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:17, cursor:"pointer" }}>🔔</button>
          </div>
        </div>
        {/* Barra compacta — siempre en DOM cuando showHero, animada via opacity/transform */}
        {showHero && (
          <div ref={compactRef} style={{
            padding:"6px 16px 8px", display:"flex", justifyContent:"space-between", alignItems:"center",
            background:P.ingresos.base, margin:"6px 16px 0", borderRadius:14,
            opacity: 0, transform:"translateY(-8px)",
            transition:"opacity 0.2s ease, transform 0.2s ease",
            willChange:"opacity, transform",
            pointerEvents:"none",
          }}>
            <div style={{ fontSize:9, fontWeight:700, color:"rgba(255,255,255,0.75)", textTransform:"uppercase", letterSpacing:"0.06em", fontFamily:FF }}>
              {hasCuentas ? "Si pagas todo, te queda" : "Te queda"}
            </div>
            <div style={{ fontSize:18, fontWeight:900, fontFamily:FF, fontVariantNumeric:"tabular-nums", letterSpacing:"-0.5px",
              color: hasCuentas
                ? (capitalReal - (comprometido?.fijosSinPagar || 0) - (comprometido?.tdcPendiente || 0)) >= 0 ? P.success.light : "#FCA5A5"
                : "#fff"
            }}>
              {fmt(hasCuentas ? capitalReal - (comprometido?.fijosSinPagar || 0) - (comprometido?.tdcPendiente || 0) : flujoDisponible)}
            </div>
          </div>
        )}
        {/* Section tabs */}
        {view==="mes" && section !== "resumen" && (
          <div style={{ padding:"6px 16px 0", overflowX:"auto" }}>
            <SecTabs />
          </div>
        )}
      </div>

      {/* Hero FUERA del sticky — se va con el scroll natural, animado via opacity/transform */}
      {showHero && (
        <div ref={heroRef} style={{
          background:P.surface,
          transition:"opacity 0.2s ease, transform 0.2s ease",
          willChange:"opacity, transform",
        }}>
          <MobileHero />
          <svg viewBox="0 0 375 40" xmlns="http://www.w3.org/2000/svg" style={{ display:"block", width:"100%", height:40, marginBottom:-1 }} preserveAspectRatio="none">
            <path d="M0 0 L375 0 L375 6 Q187.5 40 0 6 Z" fill={P.surface}/>
            <path d="M0 6 Q187.5 40 375 6 L375 40 L0 40 Z" fill={P.bg}/>
          </svg>
        </div>
      )}
      {!showHero && <div style={{ height:10 }} />}

      <div ref={contentRef} style={{ padding:16 }}><ErrorBoundary><ContentRouter /></ErrorBoundary></div>
      <MobileBottomNav />
    </div>
  );
}
